import { useEffect, useRef, useState } from 'react';
import { Button, Input } from 'antd';
import { useAsyncAction } from '../hooks/useAsyncAction';
import { RM as fmtRM } from '../lib/format';

// ---- currency re-exports for convenience ----
export { RM, RM0, RMk, RMkAt, dt, initials } from '../lib/format';

// ---- money entry ----------------------------------------------------------
// Amounts are typed far more often than they are read, and an ungrouped
// 980000 is genuinely hard to check at a glance. `type="number"` cannot group
// digits, so this is a text input with a decimal keypad that formats as you type
// and hands the caller back a clean numeric string. Shared by the sale and
// listing forms so a price behaves the same wherever it is entered.
export const groupMoney = (v) => {
  if (v === '' || v === null || v === undefined) return '';
  const [int, dec] = String(v).split('.');
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return dec === undefined ? grouped : `${grouped}.${dec}`;
};
// Keep digits and the FIRST decimal point; drop everything else the keypad allows.
export const parseMoney = (s) => String(s ?? '').replace(/[^\d.]/g, '').replace(/(\.\d*)\./g, '$1');

// `status="error"` paints AntD's red border, so a failed validation can mark the
// field itself instead of only announcing the problem in a toast.
export function MoneyInput({ value, onChange, disabled, placeholder, status }) {
  return (
    <Input
      inputMode="decimal"
      status={status}
      prefix={<span style={{ color: 'var(--muted)', fontSize: 13, fontWeight: 600 }}>RM</span>}
      value={groupMoney(value)}
      onChange={(e) => onChange?.(parseMoney(e.target.value))}
      disabled={disabled}
      placeholder={placeholder}
    />
  );
}

// "RM 1.25M" / "RM 980,000" — the compact form listing cards use for prices, where
// a full RM 1,250,000.00 crowds the row.
export const shortRM = (n) => (n >= 1000000
  ? `RM ${(n / 1000000).toFixed(n % 1000000 === 0 ? 0 : 2)}M`
  : `RM ${Number(n).toLocaleString('en-US')}`);

/**
 * Price line for a listing card, matching the admin portal: PROJECT rows show the
 * unit net-price range (or "View units" when nothing is priced yet), RENT shows
 * /mo, and a missing price reads "Price on request" rather than RM 0.
 * Shared by the Listings tab and the link-a-listing picker so a property is priced
 * the same wherever an agent meets it.
 */
export const listingPriceLabel = (l) => {
  if (l.listing_type === 'PROJECT') {
    const s = l.project_stats;
    if (s?.price_min != null) return s.price_min === s.price_max ? shortRM(s.price_min) : `${shortRM(s.price_min)} – ${shortRM(s.price_max)}`;
    return 'View units';
  }
  if (!l.asking_price_rm) return 'Price on request';
  return l.listing_type === 'RENT' ? `${fmtRM(l.asking_price_rm)}/mo` : fmtRM(l.asking_price_rm);
};

// ---- inline icon set (stroke = currentColor) ----
const svg = (children, s = 22) => (p) =>
  (
    <svg viewBox="0 0 24 24" width={s} height={s} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      {children}
    </svg>
  );
export const I = {
  home: svg(<><path d="M3 11l9-7 9 7" /><path d="M5 10v10h14V10" /></>),
  sales: svg(<><path d="M4 7h16M4 12h16M4 17h10" /></>),
  listings: svg(<><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18" /></>),
  earnings: svg(<><circle cx="12" cy="12" r="9" /><path d="M12 7v10M9.5 9.5h3.5a1.6 1.6 0 0 1 0 3.2h-2a1.6 1.6 0 0 0 0 3.2H14.5" /></>),
  more: svg(<><circle cx="5" cy="12" r="1.4" /><circle cx="12" cy="12" r="1.4" /><circle cx="19" cy="12" r="1.4" /></>),
  bell: svg(<><path d="M6 9a6 6 0 0 1 12 0v5l2 3H4l2-3Z" /><line x1="10.5" y1="20.5" x2="13.5" y2="20.5" /></>),
  back: svg(<polyline points="15 5 8 12 15 19" />),
  chevR: svg(<polyline points="9 6 15 12 9 18" />, 18),
  plus: svg(<path d="M12 5v14M5 12h14" />),
  download: svg(<><path d="M12 4v10" /><path d="M8 10.5l4 4 4-4" /><path d="M5 19.5h14" /></>),
  doc: svg(<><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><polyline points="14 3 14 8 19 8" /></>),
  pen: svg(<><path d="M4 20h4l10-10a2.1 2.1 0 0 0-3-3L5 17z" /><path d="M13.5 6.5l3 3" /></>),
  sun: svg(<><circle cx="12" cy="12" r="4.5" /><line x1="12" y1="2.5" x2="12" y2="5" /><line x1="12" y1="19" x2="12" y2="21.5" /><line x1="2.5" y1="12" x2="5" y2="12" /><line x1="19" y1="12" x2="21.5" y2="12" /></>, 17),
};

// Library-document categories → chip tones. Shared by the CID Forms list and
// the document workspace so a category never changes colour between them.
export const DOC_CATEGORY_TONE = { AUTHORISATION: 'brand', AGREEMENT: 'purple', FORM: 'ok', LETTER: 'warn', RECRUITMENT: 'accent' };

// ---- status chip ----
const CHIP = {
  ok: { bg: 'color-mix(in srgb,var(--ok) 16%,transparent)', fg: 'var(--ok)' },
  warn: { bg: 'color-mix(in srgb,var(--warn) 18%,transparent)', fg: 'var(--warn)' },
  bad: { bg: 'color-mix(in srgb,var(--bad) 16%,transparent)', fg: 'var(--bad)' },
  brand: { bg: 'var(--chip)', fg: 'var(--primary)' },
  accent: { bg: 'color-mix(in srgb,var(--accent) 16%,transparent)', fg: 'var(--accent)' },
  purple: { bg: 'color-mix(in srgb,var(--purple) 16%,transparent)', fg: 'var(--purple)' },
  orange: { bg: 'color-mix(in srgb,var(--orange) 18%,transparent)', fg: 'var(--orange)' },
  muted: { bg: 'color-mix(in srgb,var(--muted) 14%,transparent)', fg: 'var(--muted)' },
};
// Listing chip colours mirror the admin portal ListingsPage maps
// (LISTING_TYPE_CONFIG / STATUS_COLORS): Subsale blue, Rent green, Project
// purple, Auction orange; Active green, Under Offer amber, Sold red, Rented
// purple, Draft/Expired/Withdrawn grey.
export const LISTING_TYPE_TONE = { SUBSALE: 'brand', RENT: 'ok', PROJECT: 'purple', AUCTION: 'orange' };
export const LISTING_STATUS_TONE = { ACTIVE: 'ok', DRAFT: 'muted', UNDER_OFFER: 'warn', SOLD: 'bad', RENTED: 'purple', EXPIRED: 'muted', WITHDRAWN: 'muted' };
// keyed by normalized (UPPER, spaces→_) label so both "Completed" and "COMPLETED"/"PENDING_APPROVAL" resolve
const CHIP_BY_LABEL = {
  COMPLETED: 'ok', PAID: 'ok', VALIDATED: 'ok', APPROVED: 'ok', VERIFIED: 'ok', ACTIVE: 'ok', PUBLIC: 'ok', SIGNED: 'ok',
  // Booking forms and co-agency letters both render their live state as "Issued".
  ISSUED: 'ok',
  PENDING: 'warn', PARTIAL: 'warn', PENDING_SIGNING: 'warn', PENDING_APPROVAL: 'warn', INTERNAL: 'warn',
  // Commission states (earnings by-sale). Amber = HQ hasn't collected enough to free
  // it; blue = freed and sitting in the ledger; green = it left HQ.
  LOCKED: 'warn', PART_RELEASED: 'accent', RELEASED: 'accent', PART_PAID: 'accent',
  REJECTED: 'bad', VOIDED: 'bad', VOID: 'bad', CANCELLED: 'bad', WITHDRAWN: 'bad', EXPIRED: 'bad',
  CONVERTED: 'brand', DRAFT: 'brand',
};
export function Chip({ label, tone, children, style }) {
  const key = String(label || '').trim().toUpperCase().replace(/\s+/g, '_');
  // Unknown tones fall back to brand instead of crashing the whole page.
  const t = CHIP[tone] || CHIP[CHIP_BY_LABEL[key]] || CHIP.brand;
  return (
    <span style={{ fontSize: 11.5, fontWeight: 600, padding: '3px 9px', borderRadius: 999, background: t.bg, color: t.fg, whiteSpace: 'nowrap', ...style }}>
      {children || label}
    </span>
  );
}

/**
 * The ONLY way to fire a mutating action in this app. Wraps AntD Button with
 * useAsyncAction so the button shows a spinner + is disabled while its onClick promise
 * is in flight, and repeat taps are swallowed (no duplicate API requests).
 * `onClick` may be async and receives the click event.
 */
export function AsyncButton({ onClick, children, onError, minGapMs, ...rest }) {
  const [run, loading] = useAsyncAction(onClick || (() => {}), { minGapMs, onError });
  return (
    <Button {...rest} loading={loading} onClick={(e) => run(e)}>
      {children}
    </Button>
  );
}

// ---- misc UI ----
export function OfflineBanner({ show }) {
  if (!show) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'color-mix(in srgb,var(--warn) 14%,transparent)', color: 'var(--warn)', padding: '13px 14px', borderRadius: 10, fontSize: 12.5, fontWeight: 500 }}>
      <span style={{ width: 8, height: 8, borderRadius: 50, background: 'var(--warn)', flex: 'none' }} />
      Offline · changes queued, sync when online
    </div>
  );
}

export function Skeletons({ n = 3, height = 84 }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {Array.from({ length: n }).map((_, i) => (
        <div key={i} className="skel" style={{ height }} />
      ))}
    </div>
  );
}

export function EmptyState({ title, sub }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '44px 20px', textAlign: 'center' }}>
      <div style={{ width: 52, height: 52, borderRadius: 16, background: 'var(--chip)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)', fontSize: 22, fontWeight: 600 }}>—</div>
      <div style={{ fontSize: 13.5, fontWeight: 600 }}>{title}</div>
      {sub && <div style={{ fontSize: 12.5, color: 'var(--muted)', maxWidth: 220 }}>{sub}</div>}
    </div>
  );
}

/**
 * Debounced search box for list pages. Local state echoes keystrokes instantly;
 * `onSearch(term)` fires 350ms after the user stops typing (and immediately on clear),
 * so it can be wired straight into usePaginatedList params for server-side search.
 */
export function SearchBar({ onSearch, placeholder = 'Search…', delay = 350 }) {
  const [value, setValue] = useState('');
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const push = (v, immediate) => {
    setValue(v);
    clearTimeout(timer.current);
    if (immediate) onSearch(v.trim());
    else timer.current = setTimeout(() => onSearch(v.trim()), delay);
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 11 }}>
      <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="var(--muted)" strokeWidth="2" strokeLinecap="round" style={{ flex: 'none' }}>
        <circle cx="11" cy="11" r="7" /><line x1="16.5" y1="16.5" x2="21" y2="21" />
      </svg>
      <input
        value={value}
        onChange={(e) => push(e.target.value, false)}
        placeholder={placeholder}
        inputMode="search"
        style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: 'var(--text)' }}
      />
      {value && (
        <span onClick={() => push('', true)} style={{ flex: 'none', width: 18, height: 18, borderRadius: 50, background: 'var(--chip)', color: 'var(--muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11.5, cursor: 'pointer', lineHeight: 1 }}>
          ✕
        </span>
      )}
    </div>
  );
}

// A row already on the record stays in the list, greyed and unpickable, rather than
// vanishing — a name that disappears the moment you add it reads as a search bug.
// It also keeps paging honest: usePagedOptions ends the feed on a short page, so a
// picker that filtered its own rows out would stop early.
export const markPicked = (opts, ids, note = 'Already added') => opts.map((o) => (ids.includes(o.value)
  ? { ...o, disabled: true, sub: [o.sub, note].filter(Boolean).join(' · ') }
  : o));

// Fires onReach when the sentinel scrolls into view (infinite-scroll pagination).
export function InfiniteSentinel({ hasMore, loading, onReach }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!hasMore || !ref.current) return undefined;
    const io = new IntersectionObserver((es) => es[0]?.isIntersecting && onReach(), { rootMargin: '120px' });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [hasMore, onReach]);
  if (!hasMore) return null;
  return (
    <div ref={ref} style={{ textAlign: 'center', padding: 16, fontSize: 12.5, color: 'var(--muted)' }}>
      {loading ? 'Loading…' : ''}
    </div>
  );
}
